import { useEffect, useState } from 'react';

/**
 * PinholeTransition — true iris-out cinematic doorway.
 *
 * Lead's verbatim brief: "Tiny pinhole of light in center of screen
 * after black screen slowly opening out to the edges of the screen."
 *
 * Implementation: a perfectly round div sits dead-center of the
 * viewport. Its massive `box-shadow: 0 0 0 9999px #000` paints
 * everything OUTSIDE the circle pure black. Animating the circle's
 * width/height from 0 → 260vh over 2.4s opens a literal pinhole that
 * reveals the chain page underneath. No image swap, no fade, no flash.
 *
 * Sequence (~2.8s total):
 *   0 → 200ms     : black holds; tiny gold dot pulses at center.
 *   200 → 2600ms  : iris grows 0vh → 260vh on cubic-bezier ease-out.
 *   2600 → 2800ms : iris held fully open while overlay unmounts.
 */
const PINHOLE_HOLD_MS = 200;
const PINHOLE_OPEN_MS = 2400;
const PINHOLE_TAIL_MS = 200;

export default function PinholeTransition({ active, onComplete, imageUrl }) {
  const [phase, setPhase] = useState('idle');

  useEffect(() => {
    if (!active) { setPhase('idle'); return; }
    setPhase('closed');
    const t0 = setTimeout(() => setPhase('opening'), PINHOLE_HOLD_MS);
    const t1 = setTimeout(() => {
      setPhase('done');
      onComplete?.();
    }, PINHOLE_HOLD_MS + PINHOLE_OPEN_MS + PINHOLE_TAIL_MS);
    return () => { clearTimeout(t0); clearTimeout(t1); };
  }, [active, onComplete, imageUrl]);

  if (phase === 'idle' || phase === 'done') return null;

  const isOpening = phase === 'opening';
  // Closed: 0vh hole = no light. Opening: 260vh = larger than any
  // diagonal of the viewport so the entire screen reveals.
  const holeSize = isOpening ? '260vh' : '0vh';

  return (
    <div
      className="fixed inset-0 z-[100] pointer-events-none overflow-hidden"
      data-testid="pinhole-transition"
      data-pinhole-mode={imageUrl ? 'image' : 'default'}
      data-pinhole-phase={phase}
    >
      {/* The iris itself — a circle whose box-shadow paints the entire
          rest of the viewport pure black. Width/height grows from 0 to
          past the viewport corner, opening the pinhole. */}
      <div
        className="pinhole-mask"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: holeSize,
          height: holeSize,
          borderRadius: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'transparent',
          boxShadow: '0 0 0 9999px #000',
          transition: isOpening
            ? `width ${PINHOLE_OPEN_MS}ms cubic-bezier(0.22, 1, 0.36, 1), height ${PINHOLE_OPEN_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
            : 'none',
        }}
      />

      {/* Gold dot of light — visible during the closed phase so judges
          see the iris is about to open from a single point, not a black
          freeze. Fades out the moment the iris begins growing. */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          opacity: phase === 'closed' ? 1 : 0,
          transition: 'opacity 320ms ease-out',
        }}
      >
        <div
          style={{
            width: '5px',
            height: '5px',
            borderRadius: '50%',
            background: '#D4A84A',
            boxShadow: '0 0 12px 3px rgba(212,168,74,0.65), 0 0 28px 8px rgba(212,168,74,0.25)',
          }}
        />
      </div>
    </div>
  );
}
